import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Bell, Check, X, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const TYPE_LABELS = {
  reta_received: "Reta recibida",
  reta_accepted: "Reta aceptada",
  reta_declined: "Reta rechazada",
  reta_created: "Nueva reta disponible",
  reta_payment: "Comprobante de reta",
  tournament_created: "Nuevo torneo",
  tournament_brackets: "Brackets generados",
  tournament_payment: "Comprobante de torneo",
  payment_success: "Pago confirmado",
  payment_approved: "Pago aprobado",
  payment_proof: "Comprobante recibido",
  badge_earned: "🏆 Medalla ganada",
  club_created: "Nuevo club",
  club_updated: "Club actualizado",
  rental_created: "Reserva creada",
  court_available: "Cancha disponible",
  category_unlocked: "Categoría desbloqueada",
  account_status: "Estado de cuenta",
  security: "Seguridad",
};

const TYPE_COLORS = {
  reta_received: "text-[#FF3B30] border-l-[#FF3B30]",
  reta_accepted: "text-green-400 border-l-green-400",
  reta_declined: "text-red-400 border-l-red-400",
  reta_created: "text-cyan-300 border-l-cyan-400",
  tournament_created: "text-[#FFB800] border-l-[#FFB800]",
  tournament_brackets: "text-[#CCFF00] border-l-[#CCFF00]",
  payment_success: "text-green-400 border-l-green-400",
  payment_approved: "text-green-400 border-l-green-400",
  badge_earned: "text-[#FFB800] border-l-[#FFB800]",
  club_created: "text-purple-300 border-l-purple-400",
  club_updated: "text-blue-300 border-l-blue-400",
  rental_created: "text-cyan-300 border-l-cyan-400",
  court_available: "text-[#CCFF00] border-l-[#CCFF00]",
  category_unlocked: "text-[#CCFF00] border-l-[#CCFF00]",
  security: "text-red-400 border-l-red-400",
};

export default function NotificationBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const loadNotifications = useCallback(async () => {
    try {
      const [nRes, cRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setNotifications(nRes.data);
      setUnread(cRes.data.count);
    } catch (err) { console.error("Load notifications error:", err); }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) { console.error("Mark all read error:", err); }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setUnread((p) => Math.max(0, p - 1));
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    } catch (err) { console.error("Mark read error:", err); }
  };

  // Click a notification: mark read + navigate directly to target section.
  const onClickNotification = (n) => {
    if (!n.read) markRead(n.id);
    const meta = n.metadata || {};
    let target = n.cta_url;
    if (!target) {
      if (n.type?.startsWith("reta")) target = meta.reta_id ? `/dashboard/retas?open=${meta.reta_id}` : "/dashboard/retas";
      else if (n.type?.startsWith("tournament")) target = meta.tournament_id ? `/dashboard/torneos?open=${meta.tournament_id}` : "/dashboard/torneos";
      else if (n.type?.startsWith("club") || n.type === "court_available") target = "/dashboard/rentas";
      else if (n.type === "rental_created") target = "/dashboard/rentas";
      else if (n.type === "badge_earned") target = "/dashboard/perfil";
      else if (n.type === "post_like" || n.type === "post_comment") target = "/dashboard/comunidad";
      else target = "/dashboard";
    }
    setOpen(false);
    navigate(target);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative text-gray-400 hover:text-[#CCFF00]" data-testid="notification-bell">
            <Bell size={20} weight={unread > 0 ? "fill" : "regular"} />
            {unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center" data-testid="unread-count">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[320px] sm:w-[380px] bg-[#1A1A1A] border-white/10 p-0" data-testid="notification-panel">
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-white">{t("notifications.titulo")}</span>
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllRead} className="text-[#CCFF00] text-xs h-6 px-2" data-testid="mark-all-read">
                <Check size={12} className="mr-1" /> {t("notifications.marcar_leidas")}
              </Button>
            )}
          </div>
          <ScrollArea className="h-[70vh] max-h-[600px]">
            {notifications.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">{t("notifications.no_notif")}</p>
            ) : (
              notifications.map((n) => {
                const colorClass = TYPE_COLORS[n.type] || "text-white border-l-white/30";
                return (
                  <div
                    key={n.id}
                    className={`px-3 py-2.5 border-b border-white/5 border-l-4 cursor-pointer hover:bg-white/5 transition-colors ${!n.read ? "bg-[#CCFF00]/5" : ""} ${colorClass.split(" ").find(c => c.startsWith("border-l-")) || ""}`}
                    onClick={() => onClickNotification(n)}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 mt-1.5 shrink-0 ${!n.read ? "bg-[#CCFF00]" : "bg-transparent"}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-semibold ${colorClass.split(" ").find(c => c.startsWith("text-")) || "text-white"}`}>{n.title}</p>
                        <p className="text-xs text-gray-400 line-clamp-2">{n.message}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{n.created_at?.slice(0, 16).replace("T", " ")}</p>
                      </div>
                      <ArrowRight size={14} className="text-gray-600 self-center" />
                    </div>
                  </div>
                );
              })
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Expanded notification dialog */}
      <Dialog open={!!expanded} onOpenChange={(o) => { if (!o) setExpanded(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-white/15 text-white max-w-md" data-testid="notification-detail">
          {expanded && (
            <>
              <DialogHeader>
                <div className="text-[10px] uppercase tracking-wider text-[#CCFF00] mb-1">
                  {TYPE_LABELS[expanded.type] || expanded.type}
                </div>
                <DialogTitle className="text-xl">{expanded.title}</DialogTitle>
                <DialogDescription className="text-gray-300 whitespace-pre-wrap pt-2">
                  {expanded.message}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-xs text-gray-500">
                <div>📅 {expanded.created_at?.slice(0, 16).replace("T", " ")}</div>
                {expanded.metadata && Object.keys(expanded.metadata).length > 0 && (
                  <details className="bg-black/40 border border-white/10 p-2">
                    <summary className="cursor-pointer text-gray-400">Detalles técnicos</summary>
                    <pre className="text-[10px] mt-2 overflow-auto">{JSON.stringify(expanded.metadata, null, 2)}</pre>
                  </details>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setExpanded(null)} data-testid="notif-close">
                  <X size={14} className="mr-1" /> Cerrar
                </Button>
                <Button onClick={() => { onClickNotification(expanded); setExpanded(null); }} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="notif-goto">
                  Abrir sección <ArrowRight size={14} className="ml-1" />
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
